import { DrinkVariant } from '@/config/drinkVariants';

interface LoadingScreenProps {
  progress: number;
  onComplete?: () => void;
  currentVariant: DrinkVariant;
}

// LoadingScreen is now fully parent-controlled:
// - It stays 100% opaque whenever mounted
// - Parent (Index) unmounts it when the new variant is truly ready
// - No internal auto-exit logic that could cause premature transparency
const LoadingScreen = ({ progress, currentVariant }: LoadingScreenProps) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
      {/* Background gradient */}
      <div className="absolute inset-0 gradient-dark opacity-80" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Brand Logo */}
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm font-medium tracking-[0.3em] text-muted-foreground uppercase">
            DLC
          </span>
          <h1 className="font-display text-8xl md:text-9xl text-primary animate-pulse-glow rounded-full px-8 py-4">
            G
          </h1>
          <span className="text-xs font-light tracking-[0.2em] text-muted-foreground uppercase">
            {currentVariant.subtitle}
          </span>
        </div>

        {/* Loading bar container */}
        <div className="w-64 md:w-80">
          {/* Progress bar */}
          <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Percentage text */}
          <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
            <span className="tracking-wider uppercase">Loading</span>
            <span className="font-mono">{Math.round(progress)}%</span>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-primary/30 animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
